/**
 * Login page.
 *
 * Single sign-in option for now: Google. Email/password is intentionally
 * deferred — the dogfood ring is a Workspace-internal group; if/when we
 * onboard external users without Workspace accounts, we add the form.
 *
 * Design notes:
 *  - Centered, generous whitespace, matches the dashboard's warm palette.
 *  - Frog mascot — non-negotiable per README.
 *  - When Google login has been misconfigured, redirect startup can fail
 *    before the browser leaves the page and we surface that inline.
 */

import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { beginGoogleLogin } from "@/api/auth-client";

export function Login() {
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogle = async () => {
    setError(null);
    setPending(true);
    try {
      beginGoogleLogin(new URL("/", window.location.origin).toString());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sign-in failed");
      setPending(false);
    }
  };

  // Hint: if we navigate to /login while a session exists (e.g. user
  // bookmarked /login), bounce them to the dashboard. The router guard
  // also handles this case; this is a cheap belt-and-braces for the
  // tab-was-open-while-someone-signed-in-elsewhere scenario.
  void navigate;

  return (
    <div
      className="h-full flex items-center justify-center"
      style={{ background: "var(--bg)" }}
    >
      <div
        className="w-full max-w-sm mx-auto px-8 py-10 flex flex-col items-center"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 12,
        }}
      >
        <img
          src="/frogs/sleeping.gif"
          alt="A frog napping at a desk"
          className="w-20 h-20 mb-6 opacity-80"
        />
        <h1
          className="text-[var(--t1)] mb-2"
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "var(--text-2xl)",
            fontWeight: 600,
          }}
        >
          Welcome to Rondel
        </h1>
        <p
          className="text-[var(--t3)] text-center mb-8"
          style={{ fontFamily: "var(--font-body)", fontSize: "var(--text-sm)" }}
        >
          Sign in with your Southbridge Google account to access your desks.
        </p>

        <button
          type="button"
          onClick={handleGoogle}
          disabled={pending}
          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            background: "var(--white)",
            border: "1px solid var(--border)",
            color: "var(--t1)",
            fontFamily: "var(--font-ui)",
            fontSize: "var(--text-sm)",
            fontWeight: 500,
          }}
          onMouseDown={(e) =>
            ((e.currentTarget as HTMLElement).style.background = "var(--hover)")
          }
          onMouseUp={(e) =>
            ((e.currentTarget as HTMLElement).style.background = "var(--white)")
          }
          onMouseLeave={(e) =>
            ((e.currentTarget as HTMLElement).style.background = "var(--white)")
          }
        >
          {/* Google "G" mark — official monochrome version. */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 48 48"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              fill="#FFC107"
              d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.95 4 4 12.95 4 24s8.95 20 20 20 20-8.95 20-20c0-1.34-.14-2.65-.4-3.5z"
            />
            <path
              fill="#FF3D00"
              d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"
            />
            <path
              fill="#4CAF50"
              d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 34.9 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.5 16.2 44 24 44z"
            />
            <path
              fill="#1976D2"
              d="M43.6 20.5H42V20H24v8h11.3c-.7 2-1.9 3.7-3.5 5l6.2 5.2c-.4.4 6.7-4.9 6.7-14.2 0-1.34-.14-2.65-.4-3.5z"
            />
          </svg>
          {pending ? "Redirecting…" : "Continue with Google"}
        </button>

        {error && (
          <div
            className="w-full mt-4 px-3 py-2 rounded text-center"
            style={{
              background: "var(--err-bg)",
              color: "var(--err)",
              fontFamily: "var(--font-ui)",
              fontSize: "var(--text-xs)",
            }}
          >
            {error}
          </div>
        )}

        <p
          className="text-[var(--t4)] mt-8 text-center"
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "var(--text-xs)",
          }}
        >
          Internal Southbridge tool. No external access yet.
        </p>
      </div>
    </div>
  );
}
